'use client';

import { useEffect, useRef } from 'react';
import { observeThemeChange, readToken } from '@/lib/themeTokens';

type AtmosphereMode = 'viewport' | 'section';

interface CidAtmosphereProps {
  /** `viewport`: fixed behind inner pages. `section`: fills its positioned parent (homepage). */
  mode: AtmosphereMode;
}

interface Point {
  x: number;
  y: number;
  seed: number;
  color: number;
}

// Decorative only: constellation drift values from canideal.org trade-motion.js / homepage.js.
// Palettes are "r,g,b" palette tokens (src/styles/cid/palette*.css), resolved per theme.
const MODES = {
  viewport: {
    palette: ['--cid-atmo-blue', '--cid-atmo-cyan', '--cid-atmo-lime', '--cid-atmo-amber'],
    density: 19000,
    min: 24,
    max: 80,
    drift: [13, 11],
    reach: [175, 132],
    line: 0.19,
    pointer: true,
  },
  section: {
    palette: ['--cid-atmo-teal', '--cid-atmo-lime'],
    density: 30000,
    min: 30,
    max: 130,
    drift: [8, 7],
    reach: [135, 135],
    line: 0.14,
    pointer: false,
  },
} as const;

const LIGHT_SELECTOR = '.cid-panel, [data-cid-light]';
const FRAME_MS = 40; // ~25fps is enough for a quiet ambient drift

/**
 * Subtle ambient movement + cursor-following panel light + scroll reveal.
 * Everything stops (static frame, no light, no reveal) under reduced motion.
 */
export function CidAtmosphere({ mode }: CidAtmosphereProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return undefined;
    const config = MODES[mode];
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    const host = mode === 'section' ? canvas.parentElement : null;
    const pointer = { x: -1000, y: -1000 };
    let palette: string[] = config.palette.map((token) => readToken(token));
    let width = 0,
      height = 0,
      points: Point[] = [],
      frame = 0,
      last = 0,
      phase = 0,
      visible = true;

    const colorOf = (i: number) =>
      config.palette.length === 4
        ? i % 13 === 0
          ? 3
          : i % 8 === 0
            ? 2
            : i % 3 === 0
              ? 1
              : 0
        : i % 8 === 0
          ? 1
          : 0;
    const draw = (time: number) => {
      context.clearRect(0, 0, width, height);
      const reach = width < 760 ? config.reach[1] : config.reach[0];
      const at = points.map((p) => ({
        ...p,
        x: p.x + Math.sin(time + p.seed) * config.drift[0],
        y: p.y + Math.cos(time * 0.7 + p.seed) * config.drift[1],
      }));
      at.forEach((p, i) => {
        for (let j = i + 1; j < at.length; j++) {
          const distance = Math.hypot(p.x - at[j].x, p.y - at[j].y);
          if (distance >= reach) continue;
          const close = config.pointer && Math.hypot(p.x - pointer.x, p.y - pointer.y) < 210;
          context.strokeStyle = `rgba(${palette[config.pointer ? p.color : 0]},${(1 - distance / reach) * (close ? 0.42 : config.line)})`;
          context.lineWidth = close ? 0.9 : 0.7;
          context.beginPath();
          context.moveTo(p.x, p.y);
          context.lineTo(at[j].x, at[j].y);
          context.stroke();
        }
        const accent = p.color === (config.palette.length === 4 ? 2 : 1);
        context.fillStyle = `rgba(${palette[p.color]},${accent ? 0.5 : 0.45})`;
        context.beginPath();
        context.arc(p.x, p.y, accent ? 1.5 : 1.1, 0, Math.PI * 2);
        context.fill();
      });
    };
    const resize = () => {
      width = host ? host.clientWidth : window.innerWidth;
      height = host ? host.offsetHeight : window.innerHeight;
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      const count = Math.min(
        config.max,
        Math.max(config.min, Math.round((width * height) / config.density))
      );
      points = Array.from({ length: count }, (_, i) => ({
        x: (((i * 137.508 + 23) % 997) / 997) * width,
        y: (((i * 231.717 + 51) % 991) / 991) * height,
        seed: i * 2.4,
        color: colorOf(i),
      }));
      draw(phase);
    };
    const tick = (time: number) => {
      if (time - last >= FRAME_MS) {
        phase = time * 0.00014;
        draw(phase);
        last = time;
      }
      frame = requestAnimationFrame(tick);
    };
    const start = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      if (!reduced.matches && fine.matches && visible && !document.hidden)
        frame = requestAnimationFrame(tick);
      else draw(phase);
    };

    // Cursor-following light on panels (sets --mx/--my, read by the CSS).
    let lit: HTMLElement | null = null;
    const onPointerMove = (event: PointerEvent) => {
      if (reduced.matches || !fine.matches) return;
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      const panel = (event.target as Element | null)?.closest<HTMLElement>(LIGHT_SELECTOR) ?? null;
      if (lit && lit !== panel) {
        lit.style.removeProperty('--mx');
        lit.style.removeProperty('--my');
      }
      lit = panel;
      if (!panel) return;
      const box = panel.getBoundingClientRect();
      panel.style.setProperty('--mx', `${event.clientX - box.left}px`);
      panel.style.setProperty('--my', `${event.clientY - box.top}px`);
    };

    // Sections below the fold settle into place once (never hidden under reduced motion).
    const reveal = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.remove('cid-waiting');
          reveal.unobserve(entry.target);
        }),
      { threshold: 0.08 }
    );
    if (!reduced.matches) {
      document.querySelectorAll<HTMLElement>('[data-cid-reveal]').forEach((el) => {
        el.classList.add('cid-reveal');
        if (el.getBoundingClientRect().top > window.innerHeight) {
          el.classList.add('cid-waiting');
          reveal.observe(el);
        }
      });
    }
    const onMotionChange = () => {
      if (reduced.matches)
        document
          .querySelectorAll('.cid-waiting')
          .forEach((el) => el.classList.remove('cid-waiting'));
      start();
    };
    const visibility = host
      ? new IntersectionObserver((entries) => {
          visible = entries[0].isIntersecting;
          start();
        })
      : null;
    // Section mode follows its host box; viewport mode follows the window (the
    // html box also changes with content height, which would reshuffle points).
    let resizeFrame = 0;
    const scheduleResize = () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(resize);
    };
    const resizer = host ? new ResizeObserver(scheduleResize) : null;
    const stopThemeWatch = observeThemeChange(() => {
      palette = config.palette.map((token) => readToken(token));
      draw(phase);
    });

    if (host) {
      resizer?.observe(host);
      visibility?.observe(host);
    } else {
      window.addEventListener('resize', scheduleResize);
    }
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('visibilitychange', start);
    reduced.addEventListener('change', onMotionChange);
    fine.addEventListener('change', start);
    resize();
    start();

    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(resizeFrame);
      resizer?.disconnect();
      window.removeEventListener('resize', scheduleResize);
      lit?.style.removeProperty('--mx');
      lit?.style.removeProperty('--my');
      stopThemeWatch();
      visibility?.disconnect();
      reveal.disconnect();
      window.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('visibilitychange', start);
      reduced.removeEventListener('change', onMotionChange);
      fine.removeEventListener('change', start);
    };
  }, [mode]);

  return <canvas ref={canvasRef} className="cid-atmosphere" data-mode={mode} aria-hidden="true" />;
}
